import { getAccessToken, isImpersonating, getImpersonationTargetId } from './auth';
import { baseUrl } from './config';

const IMPERSONATION_HEADER = 'X-Impersonate-User-Id';

const createHeaders = async () => {
  const accessToken = await getAccessToken();
  signInIfNecessary(accessToken);
  const headers = {
    'Authorization': `Bearer ${accessToken.getToken()}`
  };
  if (isImpersonating()) {
    headers[IMPERSONATION_HEADER] = getImpersonationTargetId();
  }
  return headers;
}

const addHeadersToOptions = async (options = {}) => {
  const baseHeaders = await createHeaders();
  options.headers = { ...baseHeaders, ...options.headers };
  return options;
}

const signInIfNecessary = (accessToken) => {
  if (!accessToken) {
    window.location.href = '/sign-in';
  }
}

export const get = async (url) => {
  const options = await addHeadersToOptions();
  const response = await fetch(`${baseUrl}${url}`, options);

  // Check if response is ok (status in the range 200-299)
  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch (jsonError) {
      throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
    }
    const error = new Error(errorData.message || `HTTP error! status: ${response.status}`);
    error.response = response;
    error.data = errorData;
    throw error;
  }

  // Handle potential empty or non-JSON response body for 2xx status codes
  if (response.status === 204) { // No Content
    return null;
  }

  const contentType = response.headers.get("content-type");
  if (contentType && contentType.indexOf("application/json") !== -1) {
    try {
      return await response.json();
    } catch (e) {
      // If parsing fails even with correct content-type, treat as error or return null
      console.error(`Failed to parse JSON response for ${url}:`, e);
      throw new Error(`Failed to parse JSON response: ${e.message}`);
      // Alternatively, return null or an empty object/array based on expected data type
      // return url.endsWith('/logs') ? [] : null; 
    }
  } else {
    // If not JSON, return text or null depending on expectations
    console.warn(`Received non-JSON response for ${url}, returning null.`);
    return null; // Or potentially await response.text();
  }
}

// Fetch as blob for binary downloads
export const getBlob = async (url) => {
  const options = await addHeadersToOptions();
  const response = await fetch(`${baseUrl}${url}`, options);
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`HTTP error! status: ${response.status} - ${text}`);
  }
  return response.blob();
}

export const post = async (url, data) => {
  const options = await addHeadersToOptions({
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const response = await fetch(`${baseUrl}${url}`, options);
  
  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch (jsonError) {
      throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
    }
    const error = new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`);
    error.response = response;
    error.data = errorData;
    throw error;
  }

  return response.json();
}

export const postAnon = async (url, data) => {
  // No access token retrieval or auth check
  try {
    const response = await fetch(`${baseUrl}${url}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // No Authorization header
      },
      body: JSON.stringify(data),
    });

    // Check if response is ok (status in the range 200-299)
    if (!response.ok) {
      // Attempt to parse error json, otherwise throw status text
      let errorData;
      try {
        errorData = await response.json();
      } catch (jsonError) {
        // If response is not JSON, use status text
        throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
      }
      // Throw an error that includes the parsed message if available
      const error = new Error(errorData.message || `HTTP error! status: ${response.status}`);
      error.response = response; // Attach response for further inspection
      error.data = errorData; // Attach parsed data
      throw error;
    }

    // Handle potential empty response body for 2xx status codes
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.indexOf("application/json") !== -1) {
      return response.json();
    } else {
      // Return null or response text if not JSON
      return null; // Or await response.text(); based on expected non-JSON responses
    }
  } catch (error) {
    // Log and re-throw network errors or errors from the try block
    console.error(`API call failed for ${url}:`, error);
    throw error;
  }
};

export const postFile = (url, file, { progressCallback, headers: customHeaders }) => {
  return new Promise(async (resolve, reject) => {
    const baseHeaders = await createHeaders();

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${baseUrl}${url}`);

    for (const key in baseHeaders) {
      xhr.setRequestHeader(key, baseHeaders[key]);
    }

    for (const key in customHeaders) {
      xhr.setRequestHeader(key, customHeaders[key]);
    }

    let currentProgress = 0;
    let processingInterval = null;
    let processingStarted = false;

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        const rawPercent = (e.loaded / e.total) * 100;
        const uploadMax = 61;
        const nextProgress = (rawPercent / 100) * uploadMax;

        if (nextProgress > currentProgress) {
          currentProgress = nextProgress;
          progressCallback(parseFloat(currentProgress.toFixed(2)));
        }

        if (!processingStarted && e.loaded === e.total) {
          processingStarted = true;
          const processingTarget = 95;
          const incrementStep = 0.2;
          const incrementDelay = 200;

          processingInterval = setInterval(() => {
            if (currentProgress < processingTarget) {
              currentProgress += incrementStep;
              progressCallback(parseFloat(currentProgress.toFixed(2)));
            } else {
              clearInterval(processingInterval);
            }
          }, incrementDelay);
        }
      }
    };

    xhr.onload = () => {
      if (processingInterval) {
        clearInterval(processingInterval);
      }

      if (xhr.status >= 200 && xhr.status < 300) {
        const finalizeStep = 1;
        const finalizeDelay = 50;

        const finalizeInterval = setInterval(() => {
          if (currentProgress < 100) {
            currentProgress += finalizeStep;
            if (currentProgress > 100) currentProgress = 100;
            progressCallback(parseFloat(currentProgress.toFixed(2)));
          } else {
            clearInterval(finalizeInterval);
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch (err) {
              resolve(xhr.responseText);
            }
          }
        }, finalizeDelay);
      } else {
        let errorData;
        try {
          errorData = JSON.parse(xhr.responseText);
        } catch (e) {
          // ignore
        }

        // Backend returns { "error": "message" } usually
        const message = errorData?.error || errorData?.message || `Upload failed with status ${xhr.status} - ${xhr.statusText}`;
        const error = new Error(message);
        error.status = xhr.status;
        error.data = errorData;
        reject(error);
      }
    };

    xhr.onerror = () => {
      if (processingInterval) clearInterval(processingInterval);
      reject(new Error('Network error or CORS issue occurred during file upload.'));
    };

    const formData = new FormData();
    formData.append('file', file);

    xhr.send(formData);
  });
};

// Add API functions for key management
export const createApiKey = async (name) => {
  return await post('/api/1/keys', { name });
}

export const getApiKeys = async () => {
  return await get('/api/1/keys');
}

// Add delete function
export const del = async (url) => {
  const options = await addHeadersToOptions({ method: 'DELETE' });
  const response = await fetch(`${baseUrl}${url}`, options);
  if (response.status === 204) {
    return null;
  }
  if (response.ok) {
    try {
      return await response.json();
    } catch (e) {
      return await response.text();
    }
  } else {
    const errorBody = await response.text();
    throw new Error(`API call failed with status ${response.status}: ${errorBody}`);
  }
}

export const revokeApiKey = async (keyId) => {
  return await del(`/api/1/keys/${keyId}`);
}

// --- Admin API Functions ---

export const getAdminUsers = async (limit = 20, skip = 0, search = '') => {
  const params = new URLSearchParams({
    limit: limit.toString(),
    skip: skip.toString()
  });
  if (search) {
    params.append('search', search);
  }
  return await get(`/api/1/admin/users?${params.toString()}`);
}

export const getAdminJobs = async (userId = null, limit = 20, skip = 0) => {
  const params = new URLSearchParams({
    limit: limit.toString(),
    skip: skip.toString()
  });
  if (userId) {
    params.append('user_id', userId);
  }
  const url = `/api/1/admin/jobs?${params.toString()}`;
  return await get(url);
}

export const getAdminGenerationJobs = async (userId = null, limit = 20, skip = 0) => {
  const params = new URLSearchParams({
    limit: limit.toString(),
    skip: skip.toString()
  });
  if (userId) {
    params.append('user_id', userId);
  }
  const url = `/api/1/admin/generation-jobs?${params.toString()}`;
  return await get(url);
}

export const getAdminJobDetails = async (jobId) => {
  return await get(`/api/1/admin/jobs/${jobId}`);
}

export const getAdminJobLogs = async (jobId) => {
  return await get(`/api/1/admin/jobs/${jobId}/logs`);
}

export const getAdminAnalytics = async (days = 30) => {
  // Constrain to allowed values client-side as well (mirror backend)
  const allowed = [14, 30, 60, 90, 180];
  if (!allowed.includes(days)) days = 30;
  return await get(`/api/1/admin/analytics?days=${days}`);
}

// Add retry job API function
export const retryJob = async (jobId) => {
  return await post(`/api/1/job/${jobId}/retry`, {}); // POST request with empty body
}

// Add getUserUsage function if it doesn't exist, to fetch user details incl is_admin
export const getUserUsage = async () => {
  return await get('/api/1/user/usage');
}

export const put = async (url, data) => {
  const options = await addHeadersToOptions({
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const response = await fetch(`${baseUrl}${url}`, options);
  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch (jsonError) {
      throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
    }
    const error = new Error(errorData.message || `HTTP error! status: ${response.status}`);
    error.response = response;
    error.data = errorData;
    throw error;
  }
  return response.json();
};

export const getAdminConversionProjections = async () => {
  return await get('/api/1/admin/conversion-projections');
}

// ---- Profile API ----
export const getUserProfile = async () => {
  return await get('/api/1/user/profile');
}

export const updateUserProfile = async (profile) => {
  return await put('/api/1/user/profile', profile);
}

export const getUserTransactions = async () => {
  return await get('/api/1/user/transactions');
}

export const downloadInvoice = async (transactionId) => {
  return await getBlob(`/api/1/user/transactions/${transactionId}/invoice`);
}
